import { useState } from "react";
import Image from "next/image";

/**
 * Shared image component: wraps next/image with lazy loading (native default) and a shimmer
 * skeleton shown until the image has actually finished downloading, so course cards/heroes/etc.
 * never show a blank hole while an image streams in. Use this everywhere in place of a bare
 * <Image> unless a page truly needs eager/priority loading with no skeleton (e.g. above-the-fold
 * hero images already using `priority`, which download fast enough that a skeleton would just flash).
 */
const LazyImage = ({
  src,
  alt,
  width,
  height,
  fill = false,
  className = "",
  wrapperClassName = "",
  wrapperStyle,
  sizes,
  priority = false,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <span
      className={`rbt-lazy-image-wrapper ${loaded ? "is-loaded" : "is-loading"} ${wrapperClassName}`}
      style={{
        // fill mode: absolute+inset:0 against the nearest positioned ancestor (the caller's own
        // image container), not width/height:100% in normal flow -- a parent sized purely by
        // aspect-ratio (no explicit height) never resolves a percentage-height child, so that
        // flow-based sizing silently collapsed this wrapper (and the fill <img> positioned
        // against IT, not the container) to 0 height. Absolute positioning measures the
        // container's actual used height post-layout instead, which resolves correctly
        // regardless of how that height was computed.
        display: fill ? "block" : "inline-block",
        position: fill ? "absolute" : undefined,
        inset: fill ? 0 : undefined,
        width: fill ? undefined : width,
        height: fill ? undefined : height,
        ...wrapperStyle,
      }}
    >
      <span className="rbt-lazy-image-skeleton" aria-hidden="true" />
      <Image
        src={src}
        alt={alt}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        fill={fill}
        sizes={sizes}
        priority={priority}
        loading={priority ? undefined : "lazy"}
        className={className}
        onLoad={() => setLoaded(true)}
        {...rest}
      />
    </span>
  );
};

export default LazyImage;
